/**
 * Lokale opslag van debugclips (IndexedDB) + MediaRecorder-ringbuffer.
 * Alles blijft client-side; export kan alleen op expliciete actie.
 */

import type { ClipStoreAdapter, LabeledClip } from "./types";

const DB_NAME = "beachscore-clips";
const DB_VERSION = 1;
const STORE_CLIPS = "clips";
const STORE_FILES = "files";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_CLIPS)) {
        db.createObjectStore(STORE_CLIPS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE_FILES)) {
        db.createObjectStore(STORE_FILES, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(db: IDBDatabase, store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const req = fn(t.objectStore(store));
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error);
  });
}

export class IndexedDbClipStore implements ClipStoreAdapter {
  private dbPromise = openDb();

  async saveClip(data: Blob, meta: Omit<LabeledClip, "id" | "createdAt" | "videoKey">): Promise<string> {
    const db = await this.dbPromise;
    const id = `clip-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const clip: LabeledClip = { ...meta, id, createdAt: Date.now(), videoKey: `vid-${id}` };
    await tx(db, STORE_CLIPS, "readwrite", (s) => s.put(clip));
    await tx(db, STORE_FILES, "readwrite", (s) => s.put({ id: clip.videoKey, blob: data }));
    return id;
  }

  async listClips(): Promise<LabeledClip[]> {
    const db = await this.dbPromise;
    const all = await tx<LabeledClip[]>(db, STORE_CLIPS, "readonly", (s) => s.getAll());
    return all.sort((a, b) => b.createdAt - a.createdAt);
  }

  async setLabel(clipId: string, label: LabeledClip["label"], reason: LabeledClip["reason"], note = ""): Promise<void> {
    const db = await this.dbPromise;
    const clip = await tx<LabeledClip | undefined>(db, STORE_CLIPS, "readonly", (s) => s.get(clipId));
    if (!clip) throw new Error("clip niet gevonden");
    clip.label = label;
    clip.reason = reason;
    clip.note = note;
    await tx(db, STORE_CLIPS, "readwrite", (s) => s.put(clip));
  }

  async deleteClip(clipId: string): Promise<void> {
    const db = await this.dbPromise;
    const clip = await tx<LabeledClip | undefined>(db, STORE_CLIPS, "readonly", (s) => s.get(clipId));
    if (clip) await tx(db, STORE_FILES, "readwrite", (s) => s.delete(clip.videoKey));
    await tx(db, STORE_CLIPS, "readwrite", (s) => s.delete(clipId));
  }

  async exportManifest(): Promise<{ clips: LabeledClip[] }> {
    return { clips: await this.listClips() };
  }

  async clear(): Promise<void> {
    const db = await this.dbPromise;
    await tx(db, STORE_CLIPS, "readwrite", (s) => s.clear());
    await tx(db, STORE_FILES, "readwrite", (s) => s.clear());
  }
}

/** Ringbuffer die continu een kort WebM-segment opneemt (MediaRecorder). */
export class ClipCapture {
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];

  get supported(): boolean {
    return typeof MediaRecorder !== "undefined";
  }

  start(stream: MediaStream, sliceMs: number): void {
    if (!this.supported) return;
    this.stop();
    this.recorder = new MediaRecorder(stream, { mimeType: pickMime(), videoBitsPerSecond: 2_500_000 });
    this.chunks = [];
    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
      // houd alleen de laatste ~8 seconden
      while (this.chunks.length > 16) this.chunks.shift();
    };
    this.recorder.start(sliceMs);
  }

  /** Pak de laatste ~N seconden als blob. */
  snapshot(): Blob | null {
    if (!this.recorder || this.chunks.length === 0) return null;
    const mime = this.recorder.mimeType || "video/webm";
    return new Blob(this.chunks, { type: mime });
  }

  stop(): void {
    try {
      this.recorder?.stop();
    } catch {
      /* negeren */
    }
    this.recorder = null;
  }

  dispose(): void {
    this.stop();
    this.chunks = [];
  }
}

function pickMime(): string {
  const candidates = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm", "video/mp4"];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return "";
}

/** Download een blob als bestand (lokale export, geen upload). */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
